/**
 * 篮球的画布绘制（Canvas 2D）：侧视场地 360×640（y 向下，规则核心的逻辑单位）。只画，不改状态。
 * 颜色全从主题令牌读（readPalette，与贪吃同一套），深色模式跟着变；分数、时间、暂停、结算都在 DOM 里。
 * - TA：后爪中点（锚点）站在地面上，按 BASKET_PET_HEIGHT 定比例；比例按持球稳定姿势的主体高定，起跳帧在画布里上移、锚点不动（F0 修订 B1）。
 * - 球：飞着、落在地上时画规则核心里的那颗（判定用的就是它，画面与得分一致）；在手里时画在调用方给的点上（动作帧的附着点，B4），没给就画规则的持球点。
 * - 蓄力：TA 身后一根力度条，0→1→0 来回；不画「刚好的力度」提示，时机就是玩法。
 * - 擦框、压哨：筐沿描粗一圈，表示紧张；不换动作段（B3、B6）。
 */
import { BALL_R, HOOP_X, RIM_HALF, RIM_Y, THREE_POINT_DISTANCE, type BasketState } from "../core";
import { drawFloat, type Palette } from "./draw";
import type { FloatText } from "./feedback";
import type { SpriteSpec } from "./sprite";
import type { Fit } from "./viewport";

/** 场地的逻辑尺寸（与规则核心一致） */
export const BASKET_ARENA = { width: 360, height: 640 } as const;
/** TA 在场上画多高（持球站直时的主体高，场地单位）：出手点在脚下 90，大约在胸前 */
export const BASKET_PET_HEIGHT = 120;

export interface BasketPlacement {
  x: number;
  y: number;
  width: number;
  height: number;
  /** 图片像素 → 场地单位 */
  scale: number;
}

/** 这张图在场地上的位置与大小：锚点站在 (petX, floorY)；referenceContentHeight 是定比例用的主体高（动作帧用持球稳定姿势的，静态图用自己的）。 */
export function basketSpritePlacement(spec: SpriteSpec, petX: number, floorY: number, referenceContentHeight = spec.contentHeight): BasketPlacement {
  const scale = BASKET_PET_HEIGHT / referenceContentHeight;
  return { x: petX - spec.anchorX * scale, y: floorY - spec.anchorY * scale, width: spec.width * scale, height: spec.height * scale, scale };
}

/** 动作帧上的球附着点（画布比例）→ 场地坐标 */
export function holdPointOnCourt(placement: BasketPlacement, hold: { x: number; y: number }, canvas: { width: number; height: number }): { x: number; y: number } {
  return { x: placement.x + hold.x * canvas.width * placement.scale, y: placement.y + hold.y * canvas.height * placement.scale };
}

/** 此刻球画在哪：不在手里画规则的位置；在手里画调用方给的点（附着点、接球途中），没给画规则的持球点。 */
export function basketBallAt(state: BasketState, inPaw: { x: number; y: number } | null): { x: number; y: number } {
  if (state.ball.state !== "held" || !inPaw) return { x: state.ball.x, y: state.ball.y };
  return inPaw;
}

export interface BasketSceneInput {
  state: BasketState;
  fit: Fit;
  dpr: number;
  palette: Palette;
  /** TA 的图（已加载）与定比例用的主体高；null＝画兜底的圆点小脸 */
  sprite: { spec: SpriteSpec; image: CanvasImageSource; referenceContentHeight: number } | null;
  /** 球画在哪（basketBallAt 算好的） */
  ball: { x: number; y: number };
  /** 筐沿紧张（擦框、压哨后到判完之前） */
  tense: boolean;
  floats: readonly FloatText[];
  nowMs: number;
  reducedMotion: boolean;
}

function drawCourt(ctx: CanvasRenderingContext2D, state: BasketState, p: Palette, tense: boolean) {
  const { width, height } = BASKET_ARENA;
  ctx.fillStyle = p.floor;
  ctx.fillRect(0, 0, width, height);
  // 地面
  ctx.fillStyle = p.grid;
  ctx.fillRect(0, state.floorY, width, height - state.floorY);
  ctx.strokeStyle = p.wall;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, state.floorY);
  ctx.lineTo(width, state.floorY);
  ctx.stroke();
  // 三分线：离筐 220 处地上一道虚线（线上算三分）
  const three = HOOP_X - THREE_POINT_DISTANCE;
  ctx.save();
  ctx.strokeStyle = p.gate;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(three, state.floorY);
  ctx.lineTo(three, state.floorY - 28);
  ctx.stroke();
  ctx.restore();
  // 篮架：立柱、篮板、筐沿、网
  ctx.fillStyle = p.wall;
  ctx.fillRect(350, 380, 6, state.floorY - 380);
  ctx.fillRect(HOOP_X + RIM_HALF, RIM_Y - 60, 6, 80);
  ctx.strokeStyle = p.treat;
  ctx.lineWidth = tense ? 6 : 4;
  ctx.beginPath();
  ctx.moveTo(HOOP_X - RIM_HALF, RIM_Y);
  ctx.lineTo(HOOP_X + RIM_HALF, RIM_Y);
  ctx.stroke();
  ctx.strokeStyle = p.info;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i <= 4; i++) {
    const top = HOOP_X - RIM_HALF + (i * RIM_HALF * 2) / 4;
    const bottom = HOOP_X - RIM_HALF * 0.6 + (i * RIM_HALF * 1.2) / 4;
    ctx.moveTo(top, RIM_Y);
    ctx.lineTo(bottom, RIM_Y + 30);
  }
  ctx.stroke();
}

function drawPet(ctx: CanvasRenderingContext2D, input: BasketSceneInput) {
  const { state, palette: p, sprite } = input;
  // 地上的影子：起跳时也留在原地
  ctx.fillStyle = p.shadow;
  ctx.beginPath();
  ctx.ellipse(state.pet.x, state.floorY, 30, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  if (sprite) {
    const at = basketSpritePlacement(sprite.spec, state.pet.x, state.floorY, sprite.referenceContentHeight);
    ctx.drawImage(sprite.image, at.x, at.y, at.width, at.height);
    return;
  }
  // 兜底：圆点小脸，站在地上
  ctx.fillStyle = p.pet;
  ctx.beginPath();
  ctx.arc(state.pet.x, state.floorY - 34, 30, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = p.ink;
  for (const dx of [6, 18]) {
    ctx.beginPath();
    ctx.arc(state.pet.x + dx, state.floorY - 40, 3, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawPower(ctx: CanvasRenderingContext2D, state: BasketState, p: Palette) {
  if (state.power === null) return;
  const x = state.pet.x - 46;
  const top = state.floorY - 130;
  const h = 100;
  ctx.fillStyle = p.grid;
  ctx.fillRect(x, top, 8, h);
  ctx.fillStyle = p.good;
  ctx.fillRect(x, top + h * (1 - state.power), 8, h * state.power);
  ctx.strokeStyle = p.wall;
  ctx.lineWidth = 1;
  ctx.strokeRect(x, top, 8, h);
}

function drawBall(ctx: CanvasRenderingContext2D, at: { x: number; y: number }, p: Palette) {
  ctx.fillStyle = p.crate;
  ctx.beginPath();
  ctx.arc(at.x, at.y, BALL_R, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = p.crateLine;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(at.x - BALL_R, at.y);
  ctx.lineTo(at.x + BALL_R, at.y);
  ctx.moveTo(at.x, at.y - BALL_R);
  ctx.lineTo(at.x, at.y + BALL_R);
  ctx.stroke();
}

export function drawBasketScene(ctx: CanvasRenderingContext2D, input: BasketSceneInput): void {
  const { state, fit, dpr, palette: p } = input;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.setTransform(dpr * fit.scale, 0, 0, dpr * fit.scale, dpr * fit.offsetX, dpr * fit.offsetY);
  drawCourt(ctx, state, p, input.tense);
  drawPower(ctx, state, p);
  drawPet(ctx, input);
  drawBall(ctx, input.ball, p);
  for (const f of input.floats) drawFloat(ctx, f, input);
}
